import { useHubble } from '@seek/hubble';
import { PageName, type SolMetadata } from '@seek/sol-js';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

const useHubbleImpressionTracker = (
  jobId: string,
  solMetadata: SolMetadata,
) => {
  const ref = useRef<HTMLElement>();
  // Hubble Tracking
  const hubble = useHubble();
  const hasSentImpression = useRef<boolean>(false);
  const timeoutId = useRef<number>();

  // Note: consider to enable intersection observer v2 feature if available
  const [inViewRef, inView] = useInView({
    root: null,
    rootMargin: '0px',
    threshold: [0.5],
  });

  useEffect(
    () => () => {
      if (timeoutId.current) {
        window.clearTimeout(timeoutId.current);
      }
    },
    [],
  );

  if (inView && !timeoutId.current && !hasSentImpression.current) {
    timeoutId.current = window.setTimeout(() => {
      window.requestIdleCallback(() => {
        if (!hasSentImpression.current) {
          hasSentImpression.current = true;
          hubble.trackEvent('job_ad_snippet_viewed', {
            jobId: jobId?.toString(),
            solMetadata,
            currentPage: PageName.RECOMMENDATIONS,
          });
        }
      });
    }, 1000);
  } else if (!inView && timeoutId.current) {
    window.clearTimeout(timeoutId.current);

    timeoutId.current = undefined;
  }

  const setRefs = useCallback(
    (node: HTMLElement) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  return { setRefs };
};

export default useHubbleImpressionTracker;
